import { SpaceBetween, Button, Badge } from "@amzn/awsui-components-react";
import { useContext, useState } from "react";
import { PaymentSection } from "./PaymentSection";
import { ProductSection } from "./ProductSection";
import { MetadataSection } from "./MetadataSection";
import { SummarySection } from "./SummarySection";
import { ExpandableDbdSection } from "./ExpandableDbdSection";
import { DigitalBusinessDocument } from "../../../types/documents";
import { useExpandableSections } from "../../../hooks/useExpandableSections";
import { ActionButtons } from "../../common/ActionButtons";
import { StageContext } from "../../../contexes/OrderIdContext";
import { BADGE_COLORS } from "../../../helpers/errorUtils";

interface DBDSummaryProps {
  dbd: DigitalBusinessDocument;
  modalHandler: () => void;
}

export const DBD_INITIAL_SECTIONS = {
  payment: true,
  product: true,
  metadata: true,
} as const;

export default function DigitalBusinessDocumentView({
  dbd,
  modalHandler,
}: DBDSummaryProps) {
  const { expandedSections, toggleAllSections: originalToggleAll, handleSectionChange } =
    useExpandableSections(DBD_INITIAL_SECTIONS);

  const { payments, products } = dbd.body.body;
  const [productExpanded, setProductExpanded] = useState<Record<string, boolean>>({});
  const [allProductsExpanded, setAllProductsExpanded] = useState(true);

  const toggleAllProducts = (event: any) => {
    event.stopPropagation();
    const newState = !allProductsExpanded;
    setAllProductsExpanded(newState);
    const newProductStates = Object.keys(products ?? {}).reduce((acc, productId) => {
      acc[productId] = newState;
      return acc;
    }, {} as Record<string, boolean>);
    setProductExpanded(newProductStates);
  };

  const toggleAllSections = (expand: boolean) => {
    originalToggleAll(expand);
    const newProductStates = Object.keys(products ?? {}).reduce((acc, productId) => {
      acc[productId] = expand;
      return acc;
    }, {} as Record<string, boolean>);
    setProductExpanded(newProductStates);
  };

  const paymentId = Object.keys(payments ?? {})[0];
  const payment = payments && paymentId ? payments[paymentId] : undefined;
  const stage = useContext(StageContext);

  return (
    <SpaceBetween size="l">
      <ActionButtons
        modalHandler={modalHandler}
        toggleAllSections={toggleAllSections}
        showRedrive={true}
        copyConfig={
          dbd.raw_payload
            ? {
                textToCopy: JSON.stringify(dbd.raw_payload, null, 2),
                buttonText: "Copy for Redrive",
                successText: "Raw Dynamo JSON for redrives copied",
                errorText: "Error copying JSON to clipboard",
              }
            : undefined
        }
      />
      <SummarySection dbd={dbd} />
      <PaymentSection
        payment={payment}
        paymentId={paymentId}
        expanded={expandedSections.payment}
        onChange={handleSectionChange("payment")}
      />
      <ExpandableDbdSection
        title="Products"
        expanded={expandedSections.product}
        onChange={handleSectionChange("product")}
        description={`${Object.keys(products ?? {}).length} product(s)`}
        headerActions={
          <Button
            onClick={toggleAllProducts}
            iconName={allProductsExpanded ? "zoom-out" : "zoom-in"}
            variant="normal"
          >
            {allProductsExpanded ? "Collapse All Products" : "Expand All Products"}
          </Button>
        }
      >
        <SpaceBetween size="l">
          {Object.entries(products ?? {}).sort(([a], [b]) => a.localeCompare(b)).map(([productId, product], index) => (
            <ProductSection
              key={productId}
              product={product}
              productId={productId}
              expanded={productExpanded[productId] ?? true}
              onChange={(event) => 
                setProductExpanded(prev => ({
                  ...prev,
                  [productId]: event.detail.expanded
                }))
              }
              marketplaceId={dbd.body.header.marketplaceId}
              stage={stage}
              title={
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <Badge color={BADGE_COLORS[index % BADGE_COLORS.length]}>{index + 1}</Badge>
                  {productId}
                </SpaceBetween>
              }
            />
          ))}
        </SpaceBetween>
      </ExpandableDbdSection>
      <MetadataSection
        metadata={dbd.body.metadata}
        expanded={expandedSections.metadata}
        onChange={handleSectionChange("metadata")}
      />
    </SpaceBetween>
  );
}
